/**
 * Eric 2026-07-30 (B609 enhancement, 2e itération) — Utilitaire partagé
 * pour surligner les matches d'un terme de recherche dans un texte.
 *
 * Utilisé initialement par l'Explorateur/Textes pour highlight le search
 * dans le titre + `content_preview`. Extensible aux autres pages (Videos,
 * Base de connaissances) pour cohérence UX.
 *
 * Design :
 *   - `<mark>` HTML natif (accessibilité screen-reader).
 *   - Fond amber pâle + texte amber foncé (safe daltonien, contraste AA).
 *   - Case-insensitive, échappe les caractères RegExp (« ( », « [ »…).
 *   - `query` vide/nul → retourne le texte tel quel (aucun match).
 *   - Tâche 1165 (Eric 2026-06-17) : insensible aux ACCENTS aussi. Sans cela,
 *     la recherche trouvait « intérêt » en tapant « interet » mais ne
 *     surlignait rien — l'utilisateur croyait à un faux résultat. Le texte
 *     affiché garde ses accents : on surligne la tranche d'origine.
 */
import React from "react";
import { foldAccents, foldWithMap } from "./textFold"; // Tâche 1165

export const escapeRegExp = (s) => (s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * @param {{ text: string, query: string, testid?: string, markClassName?: string }} props
 */
export function HighlightedText({ text, query, testid, markClassName }) {
  if (text === null || text === undefined) return null;
  const q = (query || "").trim();
  if (!q) return <>{text}</>;
  const needle = foldAccents(q);
  const { folded, map, chars } = foldWithMap(text);
  const cls = markClassName || "bg-amber-100 text-amber-900 rounded px-0.5";

  // Découpe le texte d'ORIGINE aux positions trouvées dans le texte replié.
  const parts = [];
  let cursor = 0; // index dans `chars`
  let from = 0; // index dans `folded`
  let matchIndex = 0;
  for (;;) {
    const hit = needle ? folded.indexOf(needle, from) : -1;
    if (hit === -1) break;
    const start = map[hit];
    const endFolded = hit + needle.length - 1;
    const end = map[endFolded] + 1;
    if (start > cursor) {
      parts.push({ text: chars.slice(cursor, start).join(""), match: false });
    }
    parts.push({
      text: chars.slice(start, end).join(""),
      match: true,
      key: matchIndex,
    });
    matchIndex += 1;
    cursor = end;
    from = endFolded + 1;
  }
  if (cursor < chars.length) {
    parts.push({ text: chars.slice(cursor).join(""), match: false });
  }

  return (
    <>
      {parts.map((part, i) =>
        part.match ? (
          <mark
            key={i}
            className={cls}
            data-testid={testid ? `${testid}-match-${part.key}` : undefined}
          >
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </>
  );
}
